<template>
  <div ref="dropdownRef" class="relative inline-block" :class="[$props.class]">
    <!-- Trigger Button -->
    <button
      ref="triggerRef"
      type="button"
      :class="triggerClasses"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-label="ariaLabel"
      @click="toggleDropdown"
      @keydown="handleKeyDown"
    >
      <Icon
        :name="triggerIconName"
        :size="props.compact ? '20' : '22'"
      />
      <template v-if="!props.compact">
        <span
          class="text-base font-semibold"
          :class="dark ? 'text-white/80' : 'text-foreground'"
        >
          {{ currentLang?.code?.split('-')[0]?.toUpperCase() || '' }}
        </span>
        <Icon
          name="tabler:chevron-down"
          size="18"
          class="transition-transform duration-200"
          :class="[
            dark ? 'text-white/50' : 'text-muted-foreground',
            { 'rotate-180': isOpen },
          ]"
        />
      </template>
    </button>

    <!-- Desktop Dropdown — always light bg regardless of dark prop -->
    <Transition :css="false" @enter="onEnter" @leave="onLeave">
      <div
        v-if="isOpen && !isMobile"
        class="absolute z-50 w-72 overflow-hidden rounded-2xl bg-white shadow-2xl"
        :class="[
          dropdownDirection === 'down' ? 'top-full mt-2' : 'bottom-full mb-2',
          props.dropdownAlign === 'left' ? 'left-0' : 'right-0',
        ]"
      >
        <LanguageSelectorList
          ref="desktopListRef"
          :locales="locales"
          :current-locale="currentLocale"
          :show-search="showSearchDesktop"
          :search-placeholder="searchPlaceholder"
          :no-results-text="noResultsText"
          :dark="false"
          compact
          @select="handleSelect"
        />
      </div>
    </Transition>

    <!-- Mobile Full-Screen Overlay (non-dark contexts use FullscreenOverlay) -->
    <FullscreenOverlay
      v-if="!dark"
      :open="isOpen && isMobile"
      :title="ariaLabel"
      @update:open="handleMobileClose"
    >
      <LanguageSelectorList
        ref="mobileListRef"
        :locales="locales"
        :current-locale="currentLocale"
        show-search
        :search-placeholder="searchPlaceholder"
        :no-results-text="noResultsText"
        @select="handleSelect"
      />
    </FullscreenOverlay>

    <!-- Mobile Full-Screen Overlay (dark variant — website footer) -->
    <Teleport v-if="dark" to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isOpen && isMobile"
          class="fixed inset-0 z-[100] flex flex-col bg-foreground"
        >
          <!-- Dark Modal Header -->
          <div class="flex items-center justify-between border-b border-white/10 px-5 py-5">
            <h2 class="text-xl font-semibold text-white">
              {{ ariaLabel }}
            </h2>
            <button
              type="button"
              class="flex h-10 w-10 items-center justify-center rounded-xl transition-colors hover:bg-white/10"
              @click="closeDropdown"
            >
              <Icon name="tabler:x" size="22" class="text-white" />
            </button>
          </div>

          <LanguageSelectorList
            ref="mobileListRef"
            :locales="locales"
            :current-locale="currentLocale"
            show-search
            :search-placeholder="searchPlaceholder"
            :no-results-text="noResultsText"
            dark
            @select="handleSelect"
          />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import { gsap } from 'gsap';
import type { LocaleInfo } from '../../../types/locale';
import { langToFlagCode } from '../../../utils/locale';
import LanguageSelectorList from './LanguageSelectorList.vue';
import FullscreenOverlay from '../fullscreen-overlay/FullscreenOverlay.vue';

const props = withDefaults(
  defineProps<{
    /** Available locales */
    locales: LocaleInfo[];
    /** Currently selected locale code */
    currentLocale: string;
    /** Accessible label for the trigger button */
    ariaLabel?: string;
    /** Placeholder for search input */
    searchPlaceholder?: string;
    /** Text when no search results */
    noResultsText?: string;
    /** Dark mode (for dark backgrounds like website footer) */
    dark?: boolean;
    /** Compact mode — icon-only trigger (website header) */
    compact?: boolean;
    /** Show globe icon instead of flag in trigger */
    globe?: boolean;
    /** Show search in the desktop dropdown */
    showSearch?: boolean;
    /** Dropdown expand direction */
    expandDirection?: 'up' | 'down' | 'auto';
    /** Dropdown horizontal alignment */
    dropdownAlign?: 'left' | 'right';
    /** Optional CSS class for root element */
    class?: string;
  }>(),
  {
    ariaLabel: 'Select language',
    searchPlaceholder: 'Search languages...',
    noResultsText: 'No languages found',
    dark: false,
    compact: false,
    globe: false,
    showSearch: true,
    expandDirection: 'auto',
    dropdownAlign: 'right',
  },
);

const emit = defineEmits<{
  select: [code: string];
}>();

// State
const isOpen = ref(false);
const isMobile = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLElement | null>(null);
const desktopListRef = ref<InstanceType<typeof LanguageSelectorList> | null>(null);
const mobileListRef = ref<InstanceType<typeof LanguageSelectorList> | null>(null);
const dropdownDirection = ref<'up' | 'down'>('down');

const MOBILE_BREAKPOINT = 768; // md breakpoint

// Computed
const currentLang = computed(() =>
  props.locales.find((l) => l.code === props.currentLocale),
);

const showSearchDesktop = computed(() => props.showSearch ?? true);

const triggerIconName = computed(() => {
  if (props.compact || props.globe) return 'tabler:world';
  return `circle-flags:lang-${langToFlagCode(currentLang.value?.code || '')}`;
});

const triggerClasses = computed(() => {
  if (props.compact) {
    return 'flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer';
  }

  // outline-none is unconditional — kills browser default outline on mouse-click focus too
  // border is excluded from base so dark mode can be border-free
  const base =
    'inline-flex items-center justify-center gap-2.5 rounded-full px-4 py-2.5 font-medium transition-colors cursor-pointer outline-none';

  if (props.dark) {
    // No border, no focus ring — background-only pill on dark surfaces
    return `${base} bg-white/8 text-white/80 hover:bg-white/14 hover:text-white`;
  }

  return `${base} border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`;
});

// GSAP dropdown animations
function onEnter(el: Element, done: () => void) {
  gsap.set(el, { opacity: 0, y: -10, scale: 0.95, transformOrigin: 'top center' });
  gsap.to(el, {
    opacity: 1,
    y: 0,
    scale: 1,
    duration: 0.28,
    ease: 'back.out(1.6)',
    onComplete: done,
  });
}

function onLeave(el: Element, done: () => void) {
  gsap.to(el, {
    opacity: 0,
    y: -8,
    scale: 0.96,
    duration: 0.16,
    ease: 'power2.in',
    onComplete: done,
  });
}

// Methods
const checkMobile = () => {
  isMobile.value = window.innerWidth < MOBILE_BREAKPOINT;
};

const updateDropdownDirection = () => {
  if (props.expandDirection !== 'auto') {
    dropdownDirection.value = props.expandDirection as 'up' | 'down';
    return;
  }

  const triggerEl = triggerRef.value;
  if (!triggerEl) return;

  const rect = triggerEl.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;

  dropdownDirection.value =
    spaceBelow >= 300 || spaceBelow > spaceAbove ? 'down' : 'up';
};

const handleSelect = (code: string) => {
  emit('select', code);
  closeDropdown();
  triggerRef.value?.focus();
};

const closeDropdown = () => {
  if (isMobile.value && typeof document !== 'undefined') {
    document.body.style.overflow = '';
  }
  isOpen.value = false;
  desktopListRef.value?.reset();
  mobileListRef.value?.reset();
};

const handleMobileClose = (val: boolean) => {
  if (!val) closeDropdown();
};

const toggleDropdown = () => {
  if (isOpen.value) {
    closeDropdown();
  } else {
    checkMobile();
    isOpen.value = true;
    if (isMobile.value && typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
    }
  }
};

const handleKeyDown = (event: KeyboardEvent) => {
  switch (event.key) {
    case 'Escape':
      closeDropdown();
      triggerRef.value?.focus();
      break;
    case 'Tab':
      if (isOpen.value) {
        closeDropdown();
      }
      break;
  }
};

const handleClickOutside = (event: MouseEvent) => {
  if (isMobile.value) return;
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    closeDropdown();
  }
};

// Lifecycle
onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside);
  checkMobile();
  window.addEventListener('resize', checkMobile, { passive: true });
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside);
  window.removeEventListener('resize', checkMobile);
  if (typeof document !== 'undefined') {
    document.body.style.overflow = '';
  }
});

// Focus search when opening
watch(isOpen, (val) => {
  if (val) {
    updateDropdownDirection();
    if (!isMobile.value && showSearchDesktop.value) {
      nextTick(() => desktopListRef.value?.focusSearch());
    }
    if (isMobile.value) {
      nextTick(() => mobileListRef.value?.focusSearch());
    }
  }
});
</script>
